import Link from 'next/link';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './FiltrosModalMobile.module.css';

/**
 * BUSCA AVANCADA EM MODAL (modelo_busca = 12, celular).
 *
 * No cartao da busca por IA os filtros classicos ficam recolhidos e abrem inline. No
 * celular isso injetava ~894px de campos em coluna unica numa tela de 844px (medido no
 * cs2387 em 03/09) e empurrava o BUSCAR para y~1962 — o visitante perdia o botao de vista.
 * Aqui os mesmos campos passam a abrir em tela cheia, com o CTA fixo no rodape.
 *
 * PORTAL OBRIGATORIO: o cartao (`.search_form`) e' um `motion.div` do framer-motion, que
 * aplica `transform` e vira containing block — um `position: fixed` filho ficaria preso
 * dentro do cartao. Mesma receita do `DecorarImovel`/`SmartLocationSearch`.
 *
 * O componente so existe para a variante 12: quem monta decide pelo par
 * (`aiSearch` presente) + (viewport estreita). Nenhum outro hero importa este arquivo.
 */
type Props = {
    aberto: boolean;
    onFechar: () => void;
    titulo?: string;
    /** Fundo do modal. Recebe o `cor5` do tenant — o MESMO fundo que o menu dos selects
     *  ja usa hoje (`customSelectPropertiesStyles(cor_font, cor5, cor2)`), entao o par
     *  fundo/texto e' um contraste que ja esta validado em producao. */
    corFundo?: string;
    /** `cor_font` do hero (`isColorLight(cor5) ? cor11 : cor13`). */
    corTexto?: string;
    /** Destino do BUSCAR — mesmo `renderPath()` do botao classico. */
    hrefBuscar: string;
    /** Meta Pixel + fechar: roda antes de navegar. */
    aoBuscar?: () => void;
    aoLimpar?: () => void;
    children: React.ReactNode;
};

export default function FiltrosModalMobile({
    aberto,
    onFechar,
    titulo = 'Busca avançada',
    corFundo,
    corTexto,
    hrefBuscar,
    aoBuscar,
    aoLimpar,
    children,
}: Props) {
    const [montado, setMontado] = useState(false);
    const fecharRef = useRef<HTMLButtonElement | null>(null);

    useEffect(() => {
        setMontado(true);
    }, []);

    // ESC fecha, o fundo para de rolar enquanto o modal esta aberto e o foco entra no
    // botao de fechar (leitor de tela anuncia a saida antes dos 15 campos).
    useEffect(() => {
        if (!aberto || typeof document === 'undefined') return;

        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onFechar();
        };
        const overflowAnterior = document.body.style.overflow;

        document.addEventListener('keydown', onKey);
        document.body.style.overflow = 'hidden';
        fecharRef.current?.focus();

        return () => {
            document.removeEventListener('keydown', onKey);
            document.body.style.overflow = overflowAnterior;
        };
    }, [aberto, onFechar]);

    if (!montado || !aberto || typeof document === 'undefined') return null;

    const estiloCores = {
        ['--fm-bg' as any]: corFundo || '#111111',
        ['--fm-fg' as any]: corTexto || '#ffffff',
    } as React.CSSProperties;

    return createPortal(
        <div
            className={styles.overlay}
            role="dialog"
            aria-modal="true"
            aria-label={titulo}
            style={estiloCores}
        >
            <div className={styles.cabecalho}>
                <span className={styles.titulo}>{titulo}</span>
                <button
                    ref={fecharRef}
                    type="button"
                    className={styles.fechar}
                    onClick={onFechar}
                    aria-label="Fechar busca avançada"
                >
                    ✕
                </button>
            </div>

            <div className={styles.corpo}>{children}</div>

            <div className={styles.rodape}>
                <button type="button" className={styles.limpar} onClick={aoLimpar}>
                    LIMPAR
                </button>
                <Link
                    prefetch={false}
                    href={hrefBuscar}
                    className={styles.buscar}
                    onClick={aoBuscar}
                >
                    BUSCAR
                </Link>
            </div>
        </div>,
        document.body
    );
}
